import { Button, Flex, Input, List, message, Select, Spin, Card, Form, notification, Tag, Row, Col, Modal } from "antd";
import React, { useEffect, useState } from "react";
import { SearchOutlined, PlusOutlined, DeleteOutlined, CheckOutlined, EditOutlined } from "@ant-design/icons";
import { getCandidateCriteria, createCandidateCriteria, editCandidateCriteria, getCandidateCriteriaDetail } from "api/search.api";
import { useAppDispatch, useAppSelector } from "hooks/reduxHooks";
import TotalWeight from "./TotalWeight";
import CreateCriteriaForm from "./CreateCriteriaForm";

interface Template {
    id: number;
    name: string;
    user: number;
    questions: Array<{
        id: number;
        question_set: number;
        question: string;
        order: number;
        created: string;
        modified: string;
    }>;
    created: string;
    modified: string;
}

interface FormQuestion {
    key: number;
    text: string;
    id?: number;
    weight?: number;
}

const AddCandidateCriteria = ({ search_question_set_id, setSearchQuestionSetId, isDisabled = false, setIsCreating, isPopup }: any) => {
    const [newQuestion, setNewQuestion] = useState("");
    const [showAddForm, setShowAddForm] = useState(false);
    const [questions, setQuestions] = useState<string[]>([]);
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [showCreateTemplate, setShowCreateTemplate] = useState(false);
    const [templateName, setTemplateName] = useState("");
    const [formQuestions, setFormQuestions] = useState<FormQuestion[]>([{ key: 0, text: "" }]);
    const [suggestedQuestions, setSuggestedQuestions] = useState<FormQuestion[]>([]);
    const [editingTemplateId, setEditingTemplateId] = useState<number | null>(null);
    const [form] = Form.useForm();
    const dispatch = useAppDispatch();
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        loadTemplates();
    }, []);

    const loadTemplates = async () => {
        setLoading(true);
        try {
            const response = await getCandidateCriteria(true) as any;
            setTemplates(response);
        } catch (error) {
            console.error("Error loading templates:", error);
            message.error("Failed to load templates");
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = async () => {
        if (!newQuestion.trim()) {
            message.warning("Please enter a criteria");
            return;
        }

        setQuestions(prev => [...prev, newQuestion.trim()]);
        setNewQuestion("");
        setShowAddForm(false);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleAddQuestion();
        }
    };

    const handleTemplateSelect = (value: number) => {
        setSearchQuestionSetId?.(value);

        // Find the selected template
        const selectedTemplateData = templates.find(template => template.id === value);

        if (selectedTemplateData) {
            // Clear existing questions first
            setQuestions([]);

            // Add questions from the selected template
            const templateQuestions = selectedTemplateData.questions
                .sort((a, b) => a.order - b.order)
                .map(questionItem => questionItem.question);

            setQuestions(templateQuestions);
            // message.success(`Loaded "${selectedTemplateData.name}" template with ${selectedTemplateData.questions.length} criteria`);
        }
    };

    const clearAllQuestions = () => {
        setQuestions([]);
    };

    const handleClearTemplate = () => {
        setSearchQuestionSetId(null);
        clearAllQuestions();
    };

    // Create Template Functionality
    const handleShowCreateTemplate = () => {
        if (isPopup) {
            setModalOpen(true);
            setShowCreateTemplate(true);
        } else {
            setShowCreateTemplate(true);
        }
        setIsCreating?.(true);
        setEditingTemplateId(null);
        setFormQuestions([{ key: 0, text: "" }]);
        setTemplateName("");
        setSuggestedQuestions([]);
    };


    // Edit Template Functionality
    const handleEditTemplate = async (templateId: number) => {
        setLoading(true);
        try {
            const template = await getCandidateCriteriaDetail(templateId);

            // Map API questions to form questions
            const formQuestions: FormQuestion[] = template.questions.map((question: any, index: number) => ({
                key: index,
                text: question.question,
                id: question.id
            }));

            setFormQuestions(formQuestions);
            setTemplateName(template.name);
            setEditingTemplateId(templateId);
            setShowCreateTemplate(true);
            setIsCreating?.(true);
            if (isPopup) {
                setModalOpen(false);
            }

            message.success(`Editing template: ${template.name}`);
        } catch (error) {
            console.error("Error loading template for edit:", error);
            notification.error({
                message: "Error",
                description: "Failed to load template for editing",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSuccess = (templateId: number, templateName: string, questionsArray: string[]) => {
        setSearchQuestionSetId?.(templateId);
        setQuestions(questionsArray);
        setShowCreateTemplate(false);
        setIsCreating?.(false);
        setEditingTemplateId(null);
        if (isPopup) {
            setModalOpen(false);
        }

        loadTemplates(); // Reload templates list
    };


    const handleCancelTemplate = () => {
        if (isPopup) {
            setModalOpen(false);
        }

        setShowCreateTemplate(false);
        setIsCreating?.(false);
        setEditingTemplateId(null);
        setTemplateName("");
        setFormQuestions([{ key: 0, text: "" }]);
    };

    useEffect(() => {
        if (search_question_set_id) {
            handleTemplateSelect(search_question_set_id);
        }
    }, [search_question_set_id, templates]);


    // useEffect(() => {
    //     const total = formQuestions.reduce((sum, question) => sum + (question.weight || 0), 0);
    //     setTotalPercentage(total);
    // }, [formQuestions]);


    // Enhanced template options with edit actions
    const enhancedTemplateOptions = templates.map(template => ({
        value: template.id,
        label: (
            <div className="flex justify-between items-center w-full">
                <span>{template.name}</span>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                        {template.questions.length} criteria
                    </span>
                    <Button
                        type="text"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEditTemplate(template.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                    />
                </div>
            </div>
        ),
        template: template
    }));

    // Render the Create/Edit Template form
    const renderCreateTemplateForm = () => (
        <CreateCriteriaForm
            editingTemplateId={editingTemplateId}
            onSuccess={handleTemplateSuccess}
            onCancel={handleCancelTemplate}
            maxQuestions={8}
            showCancel={false}
        />
    );


    return (
        <div className="w-full">
            {isPopup && (
                <Modal
                    title={editingTemplateId ? "Edit Template" : "Create New Template"}
                    open={modalOpen}
                    onCancel={handleCancelTemplate}
                    footer={null}
                    width={800}
                    destroyOnClose
                >
                    {modalOpen && renderCreateTemplateForm()}
                </Modal>
            )}

            <Flex className="justify-between items-center">
                <div className="flex gap-2 items-center">
                    <span className="text-lg font-semibold text-gray-700">Candidate Criteria</span>
                </div>
                {!showCreateTemplate && (
                    <Flex gap={8}>
                        <Button
                            type="primary"
                            onClick={handleShowCreateTemplate}
                            size="large"
                            style={{ height: '36px' }}
                            disabled={isDisabled}
                        >
                            Create Template
                        </Button>
                    </Flex>
                )}
            </Flex>

            <div className="pt-5">
                <Flex vertical>
                    {/* Create/Edit Template Form */}
                    {!isPopup && showCreateTemplate && (
                        <Card
                            className="mb-4 shadow-sm bg-#e4e7ec"
                            title={editingTemplateId ? "Edit Template" : "Create New Template"}
                            extra={
                                <Button type="text" onClick={handleCancelTemplate}>
                                    X
                                </Button>
                            }
                        >
                            {renderCreateTemplateForm()}
                        </Card>
                    )}

                    {/* Add Criteria Form */}
                    {showAddForm && (
                        <Flex vertical className="mb-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                            <Input.TextArea
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder="Enter your criteria here (e.g., '5+ years of React experience', 'Must have TypeScript knowledge', etc.)"
                                className="mb-3"
                                size="large"
                                autoFocus
                                rows={3}
                                style={{ resize: 'vertical' }}
                            />
                            <Flex justify="end" gap={8}>
                                <Button
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setNewQuestion("");
                                    }}
                                    size="large"
                                    style={{ height: '36px' }}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddQuestion}
                                    disabled={!newQuestion.trim()}
                                    size="large"
                                    style={{ height: '36px' }}
                                >
                                    Add Criteria
                                </Button>
                            </Flex>
                        </Flex>
                    )}

                    {/* Template Selection Dropdown */}
                    <div className="mb-4">
                        <Select
                            placeholder="Select a criteria template"
                            defaultValue={templates.length > 0 ? search_question_set_id : undefined}
                            value={templates.length > 0 ? search_question_set_id : undefined}
                            onChange={handleTemplateSelect}
                            options={enhancedTemplateOptions}
                            className="w-full"
                            size="large"
                            allowClear
                            onClear={handleClearTemplate}
                            loading={loading}
                            disabled={isDisabled}
                            optionRender={(option) => (
                                <div className="flex justify-between items-center w-full group">
                                    <span>{option.data.template.name}</span>
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs text-gray-500">
                                            {option.data.template.questions.length} criteria
                                        </span>
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<EditOutlined />}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleEditTemplate(option.data.template.id);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                                        />
                                    </div>
                                </div>
                            )}
                        />
                    </div>

                    {/* Questions List */}
                    <div>
                        {questions.length === 0 || !search_question_set_id ? (
                            <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg">
                                <SearchOutlined className="text-3xl text-gray-300 mb-2" />
                                <div className="text-gray-500">No Criteria added yet</div>
                                <div className="text-sm text-gray-400">
                                    Add Criteria to screen candidates
                                </div>
                            </div>
                        ) : (
                            <List
                                dataSource={questions}
                                renderItem={(question: any, index: number) => (
                                    <List.Item
                                        className="border border-gray-100 rounded-lg mb-2 p-2 hover:bg-gray-50 transition-colors"
                                        // actions={[
                                        //     <Button
                                        //         type="text"
                                        //         danger
                                        //         key={index}
                                        //         size="large"
                                        //         icon={<DeleteOutlined />}
                                        //         onClick={() => handleDeleteQuestion(question)}
                                        //     >
                                        //         {/* Delete */}
                                        //     </Button>
                                        // ]}
                                        key={index}
                                        style={{ padding: "4px 12px" }}
                                    >
                                        <List.Item.Meta
                                            title={
                                                <div className="text-base text-gray-800">
                                                    <span className="font-semibold"> Criteria {index + 1} : </span> {question}
                                                </div>
                                            }
                                        />
                                    </List.Item>
                                )}
                                pagination={false}
                            />
                        )}
                    </div>
                </Flex>
            </div>
        </div>
    );
};

export default AddCandidateCriteria;